/**
 * A phone can keep running an older copy of the app after a deploy. When that copy asks for a
 * page file the new deploy removed, loading fails. These helpers recognise that failure and get
 * the newest copy: forget the stored copy of the app, then reload.
 */
const TRIED_KEY = "gridaction.refreshed-for-update";

/** The wording differs by browser: Chrome, Safari and Firefox each say it their own way. */
export function isStaleAppError(message: string | undefined): boolean {
  return /dynamically imported module|Loading chunk|Importing a module script failed|not a valid JavaScript MIME type|error loading dynamically/i.test(
    message ?? "",
  );
}

/** Forgets the stored copy of the app (service worker and caches), then reloads. */
export async function refreshApp(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(registrations.map((registration) => registration.unregister()));
    const names = (await globalThis.caches?.keys?.()) ?? [];
    await Promise.all(names.map((name) => globalThis.caches.delete(name)));
  } catch {
    // Reloading is still worth a try.
  }
  window.location.reload();
}

/** Does the refresh by itself, but only once per visit so a real fault can't loop. */
export function refreshAppOnce(): boolean {
  try {
    if (sessionStorage.getItem(TRIED_KEY)) return false;
    sessionStorage.setItem(TRIED_KEY, "1");
  } catch {
    return false;
  }
  void refreshApp();
  return true;
}
